import React, { useState } from "react";
import { auth, db } from "../firebase/firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  collection,
} from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./SignUpComponent.scss";

const SignUpComponent = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    bride_name: "",
    groom_name: "",
    wedding_date: "",
    ceremony_time: "",
    wedding_location: "",
    hotel_address: "",
    bride_parent_surname: "",
    groom_parent_surname: "",
    guest_count: "",
    mobile_number: "",
  });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "guest_count" || name === "mobile_number"
          ? value.replace(/\D/g, "")
          : value,
    }));
  };

  const slugify = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  const generateUniqueSlug = async (brideName, groomName) => {
    const brideSlug = slugify(brideName);
    const groomSlug = slugify(groomName);

    let baseSlug = `${brideSlug}-${groomSlug}`;

    if (!baseSlug || baseSlug === "-") {
      baseSlug = `wedding-${Date.now()}`;
    }

    let finalSlug = baseSlug;
    let counter = 2;

    while (true) {
      const siteRef = doc(db, "sites", finalSlug);
      const siteSnap = await getDoc(siteRef);

      if (!siteSnap.exists()) {
        return finalSlug;
      }

      finalSlug = `${baseSlug}-${counter}`;
      counter += 1;
    }
  };

  const getExpiryDateAfterDays = (days = 7) => {
    const date = new Date();
    date.setDate(date.getDate() + days);

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      bride_name: "",
      groom_name: "",
      wedding_date: "",
      ceremony_time: "",
      wedding_location: "",
      hotel_address: "",
      bride_parent_surname: "",
      groom_parent_surname: "",
      guest_count: "",
      mobile_number: "",
    });
  };

  const createDefaultTestFamily = async ({
    uid,
    slug,
    groomName,
    email,
  }) => {
    const guestType = `${groomName}'s Guest`;

    const familyPayload = {
      familyName: "Test Family",
      guestType,
      guests: [
        {
          id: `guest-test-1-${Date.now()}`,
          name: "Test 1",
          status: "Pending",
          takingLiquor: "Yes",
        },
        {
          id: `guest-test-2-${Date.now() + 1}`,
          name: "Test 2",
          status: "Pending",
          takingLiquor: "No",
        },
      ],
      createdBy: email || "unknown",
      createdAt: serverTimestamp(),
    };

    const privateFamilyRef = doc(collection(db, "users", uid, "GroomGuests"));
    const publicFamilyRef = doc(
      db,
      "sites",
      slug,
      "GroomGuests",
      privateFamilyRef.id
    );

    await setDoc(privateFamilyRef, familyPayload);
    await setDoc(publicFamilyRef, familyPayload);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (formData.password.length < 6) {
      setErrorMessage("Password must be at least 6 characters.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const uid = userCredential.user.uid;

      const slug = await generateUniqueSlug(
        formData.bride_name,
        formData.groom_name
      );

      const expiry_date = getExpiryDateAfterDays(7);
      const ceremony_type = "wedding";

      const defaultSections = {
        isInviteSection: true,
        isCardComponent: true,
        isCountDown: true,
        isRsvp: true,
        isAgenda: true,
        isLoveNote: true,
      };

      const userData = {
        uid,
        email: formData.email,
        slug,
        siteUrl: `/${slug}`,
        plan: "trial",
        isTrial: true,
        user_type: "normal user",
        ceremony_type,
        expiry_date,
        showImages: true,
        ...defaultSections,
        bride_name: formData.bride_name,
        groom_name: formData.groom_name,
        wedding_date: formData.wedding_date,
        ceremony_time: formData.ceremony_time || "",
        wedding_location: formData.wedding_location,
        hotel_address: formData.hotel_address,
        bride_parent_surname: formData.bride_parent_surname,
        groom_parent_surname: formData.groom_parent_surname,
        guest_count: Number(formData.guest_count),
        mobile_number: formData.mobile_number,
        createdAt: serverTimestamp(),
      };

      const siteData = {
        slug,
        ownerUid: uid,
        email: formData.email,
        user_type: "normal user",
        ceremony_type,
        expiry_date,
        showImages: true,
        ...defaultSections,
        bride_name: formData.bride_name,
        groom_name: formData.groom_name,
        wedding_date: formData.wedding_date,
        ceremony_time: formData.ceremony_time || "",
        wedding_location: formData.wedding_location,
        hotel_address: formData.hotel_address,
        bride_parent_surname: formData.bride_parent_surname,
        groom_parent_surname: formData.groom_parent_surname,
        guest_count: Number(formData.guest_count),
        mobile_number: formData.mobile_number,
        plan: "trial",
        isTrial: true,
        isPublished: true,
        createdAt: serverTimestamp(),
      };

      await setDoc(doc(db, "users", uid), userData);
      await setDoc(doc(db, "sites", slug), siteData);

      await createDefaultTestFamily({
        uid,
        slug,
        groomName: formData.groom_name,
        email: formData.email,
      });

      setSuccessMessage("Account created successfully.");
      resetForm();

      navigate(`/sites/${slug}/dashboard`);
    } catch (error) {
      console.error("Signup error:", error);

      if (error.code === "auth/email-already-in-use") {
        setErrorMessage("This email is already registered.");
      } else if (error.code === "auth/invalid-email") {
        setErrorMessage("Please enter a valid email address.");
      } else if (error.code === "auth/weak-password") {
        setErrorMessage("Password should be at least 6 characters.");
      } else {
        setErrorMessage("Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup">
      <br />
      <br />
      <br />
      <br />

      <div className="signup__card">
        <div className="signup__header">
          <p className="signup__eyebrow">Create your wedding website</p>
          <h1 className="signup__title">Sign Up</h1>
          <p className="signup__subtitle">
            Create your admin account and wedding profile
          </p>
        </div>

        <form className="signup__form" onSubmit={handleSubmit}>
          <div className="signup__grid">
            <div className="signup__field">
              <label className="signup__label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="signup__input"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="signup__field">
              <label className="signup__label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                className="signup__input"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="signup__field">
            <label className="signup__label" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              className="signup__input"
              placeholder="Confirm password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          <div className="signup__grid">
            <div className="signup__field">
              <label className="signup__label" htmlFor="bride_name">
                Bride Name
              </label>
              <input
                id="bride_name"
                name="bride_name"
                placeholder="Enter Only The First Name"
                type="text"
                className="signup__input"
                value={formData.bride_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="signup__field">
              <label className="signup__label" htmlFor="groom_name">
                Groom Name
              </label>
              <input
                id="groom_name"
                name="groom_name"
                placeholder="Enter Only The First Name"
                type="text"
                className="signup__input"
                value={formData.groom_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="signup__grid">
            <div className="signup__field">
              <label className="signup__label" htmlFor="wedding_date">
                Wedding Date
              </label>
              <input
                id="wedding_date"
                name="wedding_date"
                type="date"
                className="signup__input"
                value={formData.wedding_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="signup__field">
              <label className="signup__label" htmlFor="ceremony_time">
                Ceremony Time
              </label>
              <input
                id="ceremony_time"
                name="ceremony_time"
                type="time"
                className="signup__input"
                value={formData.ceremony_time}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="signup__field">
            <label className="signup__label" htmlFor="wedding_location">
              Wedding Location
            </label>
            <input
              id="wedding_location"
              name="wedding_location"
              type="text"
              className="signup__input"
              value={formData.wedding_location}
              onChange={handleChange}
              required
            />
          </div>

          <div className="signup__field">
            <label className="signup__label" htmlFor="hotel_address">
              Hotel Address
            </label>
            <textarea
              id="hotel_address"
              name="hotel_address"
              className="signup__textarea"
              value={formData.hotel_address}
              onChange={handleChange}
              required
            />
          </div>

          <div className="signup__field">
            <label className="signup__label" htmlFor="mobile_number">
              WhatsApp / Mobile Number
            </label>
            <input
              id="mobile_number"
              name="mobile_number"
              type="text"
              inputMode="numeric"
              className="signup__input"
              placeholder="Enter WhatsApp number"
              value={formData.mobile_number}
              onChange={handleChange}
              required
            />
          </div>

          <div className="signup__grid">
            <div className="signup__field">
              <label className="signup__label" htmlFor="bride_parent_surname">
                Bride Parent Surname
              </label>
              <input
                id="bride_parent_surname"
                name="bride_parent_surname"
                type="text"
                className="signup__input"
                value={formData.bride_parent_surname}
                onChange={handleChange}
                required
              />
            </div>

            <div className="signup__field">
              <label className="signup__label" htmlFor="groom_parent_surname">
                Groom Parent Surname
              </label>
              <input
                id="groom_parent_surname"
                name="groom_parent_surname"
                type="text"
                className="signup__input"
                value={formData.groom_parent_surname}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="signup__field">
            <label className="signup__label" htmlFor="guest_count">
              Guest Count
            </label>
            <input
              id="guest_count"
              name="guest_count"
              type="text"
              inputMode="numeric"
              className="signup__input"
              value={formData.guest_count}
              onChange={handleChange}
              required
            />
          </div>

          {errorMessage && <p className="signup__error">{errorMessage}</p>}

          {successMessage && (
            <p className="signup__success">{successMessage}</p>
          )}

          <ButtonComponent
            as="button"
            type="submit"
            className="signup__submit ui-btn"
            disabled={loading}
          >
            {loading ? "Creating account..." : "Create Account"}
          </ButtonComponent>
        </form>

        <div className="signup__footer">
          <p className="signup__footerText">
            Already have an account?{" "}
            <Link to="/login" className="signup__link">
              Go to Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpComponent;